import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../../utils/i18n/i18n";
import { STRING_LANGUAGE_COMPRARTODO } from "../../utils/const/string-const";
import { getWhatsAppChatUrl } from "../../config/public-site";
import Calendar from "../../components/calendar";
import moment from "moment";
// import { createApplicant } from "../../api/api-applicant";
import ProcessingDialog from "../../components/processing-dialog";
import ToastComponent from "../../components/toast-component";
import { bindSharedToast, unbindSharedToast, sharedShowToast } from "../../utils/shared-toast";

/** Strict parse for MM-DD-YYYY from Calendar; avoids Safari/Chrome differences with `moment(string)` only. */
const REGISTER_DATE_PARSE_FORMATS = [
    "MM-DD-YYYY",
    "M-D-YYYY",
    "MM-DD-YY",
    "YYYY-MM-DD",
    "YYYY/MM/DD",
    "MM/DD/YYYY",
    "M/D/YYYY",
];

function preferredDateToApiIso(preferredDateRaw) {
    const s = String(preferredDateRaw ?? "").trim();
    if (!s) return "";
    const parsed = moment(s, REGISTER_DATE_PARSE_FORMATS, true);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
}

export default function RegisterPage() {
    const history = useHistory();
    const { t } = useTranslation();
    const [selectedLanguage, setSelectedLanguage] = useState("en");
    const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const whatsappUrl = getWhatsAppChatUrl();

    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");
    const [preferredDate, setPreferredDate] = useState("");
    const [expectations, setExpectations] = useState("");

    const [processingVisible, setProcessingVisible] = useState(false);
    const [toastVisible, setToastVisible] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const [toastType, setToastType] = useState("warning"); // "warning" | "success" | "error"

    const [fieldErrors, setFieldErrors] = useState({});

    const clearFieldError = (key) => {
        setFieldErrors((prev) => {
            if (!prev[key]) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
        });
    };

    const languages = [
        { value: "en", label: "English", flag: "🇺🇸" },
        { value: "es", label: "Español", flag: "🇪🇸" },
        { value: "pt", label: "Português", flag: "🇧🇷" },
    ];

    const charLimits = {
        name: 128,
        phone: 45,
        email: 128,
        expectations: 1024,
        date: 10,
    };

    const minAppointmentDate = moment().format("MM-DD-YYYY");

    useEffect(() => {
        const language = localStorage.getItem(STRING_LANGUAGE_COMPRARTODO);
        if (language) {
            i18n.changeLanguage(language);
            setSelectedLanguage(language);
        } else {
            i18n.changeLanguage("en");
            setSelectedLanguage("en");
        }
    }, []);

    useEffect(() => {
        bindSharedToast({
            setMessage: setToastMessage,
            setType: setToastType,
            setVisible: setToastVisible,
        });
        return () => unbindSharedToast();
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                isLanguageDropdownOpen &&
                !event.target.closest(".language-dropdown")
            ) {
                setIsLanguageDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isLanguageDropdownOpen]);

    const handleRegisterSubmit = async (event) => {
        try {
            event.preventDefault();
            const dateStart = preferredDateToApiIso(preferredDate);
            const trimmed = {
                name: name.trim(),
                phoneNumber: phone.trim(),
                emailAddress: email.trim(),
                dateStart: dateStart,
                expectation: expectations.trim(),
            };
            const requiredMsg = t("label_required_field");
            const errors = {};
            if (!trimmed.name) errors.name = requiredMsg;
            if (!trimmed.phoneNumber) errors.phone = requiredMsg;
            if (!trimmed.emailAddress) errors.email = requiredMsg;
            if (!dateStart) errors.date = requiredMsg;
            if (Object.keys(errors).length > 0) {
                setFieldErrors(errors);
                const order = ["name", "phone", "email", "date"];
                const firstKey = order.find((k) => errors[k]);
                const focusId =
                    firstKey === "name"
                        ? "reg-name"
                        : firstKey === "phone"
                            ? "reg-phone"
                            : firstKey === "email"
                                ? "reg-email"
                                : "reg-date";
                document.getElementById(focusId)?.scrollIntoView({ block: "center", behavior: "smooth" });
                return;
            }
            setFieldErrors({});
            setProcessingVisible(true);
            // const response = await createApplicant(trimmed);
            // if (response.error === false) {
            //     sharedShowToast(t("te_register_success"), "success", 3000, () => {
            //         history.push("/landing");
            //     });
            // } else {
            //     sharedShowToast(
            //         response.message || t("te_register_failed"),
            //         "error"
            //     );
            // }
        } catch (error) {
            sharedShowToast(t("te_register_failed"), "error");
        } finally {
            setProcessingVisible(false);
        }
    };

    const LanguageDropdown = () => (
        <div className="relative language-dropdown">
            <button
                type="button"
                onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-indigo-50 transition border border-transparent hover:border-indigo-100"
                aria-expanded={isLanguageDropdownOpen}
                aria-haspopup="listbox"
            >
                <span className="text-xl" aria-hidden>
                    {languages.find((lang) => lang.value === selectedLanguage)?.flag}
                </span>
                <span className="text-slate-700 text-sm font-medium">
                    {languages.find((lang) => lang.value === selectedLanguage)?.label}
                </span>
                <svg
                    className={`w-4 h-4 text-slate-500 transition-transform ${isLanguageDropdownOpen ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>
            {isLanguageDropdownOpen && (
                <div
                    className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-slate-200 py-2 z-50"
                    role="listbox"
                >
                    {languages.map((language) => (
                        <button
                            type="button"
                            key={language.value}
                            onClick={() => {
                                setSelectedLanguage(language.value);
                                setIsLanguageDropdownOpen(false);
                                i18n.changeLanguage(language.value);
                                localStorage.setItem(STRING_LANGUAGE_COMPRARTODO, language.value);
                            }}
                            className={`w-full flex items-center space-x-3 px-4 py-2 hover:bg-indigo-50 transition text-left ${selectedLanguage === language.value ? "bg-indigo-50 text-indigo-800" : "text-slate-700"}`}
                            role="option"
                            aria-selected={selectedLanguage === language.value}
                        >
                            <span className="text-xl" aria-hidden>
                                {language.flag}
                            </span>
                            <span className="font-medium">{language.label}</span>
                            {selectedLanguage === language.value && (
                                <svg className="w-4 h-4 ml-auto text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path
                                        fillRule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );

    const currentYear = new Date().getFullYear();

    return (
        <div className="min-h-screen bg-slate-50 text-slate-900 antialiased selection:bg-indigo-200 selection:text-indigo-950">
            <div className="pointer-events-none fixed inset-0 overflow-hidden" aria-hidden>
                <div className="absolute -top-40 -right-32 h-96 w-96 rounded-full bg-indigo-400/20 blur-3xl" />
                <div className="absolute top-1/3 -left-24 h-80 w-80 rounded-full bg-violet-400/15 blur-3xl" />
            </div>

            <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/90 backdrop-blur-md shadow-sm">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-3.5 gap-3">
                        <button
                            type="button"
                            onClick={() => history.push("/landing")}
                            className="text-left font-bold text-xl sm:text-2xl tracking-tight text-indigo-900 hover:text-indigo-700 transition shrink-0"
                        >
                            {t("te_brand")}
                        </button>

                        <div className="hidden sm:flex items-center gap-4 text-sm">
                            <button
                                type="button"
                                onClick={() => history.push("/landing")}
                                className="text-slate-600 hover:text-indigo-600 font-medium transition"
                            >
                                {t("te_register_back")}
                            </button>
                        </div>

                        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                            <button
                                type="button"
                                className="sm:hidden p-2 rounded-lg text-slate-700 hover:bg-slate-100 border border-slate-200"
                                onClick={() => setMobileNavOpen((o) => !o)}
                                aria-expanded={mobileNavOpen}
                                aria-label="Menu"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    {mobileNavOpen ? (
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    ) : (
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                    )}
                                </svg>
                            </button>
                            <div className="hidden sm:block">
                                <LanguageDropdown />
                            </div>
                            <button
                                type="button"
                                onClick={() => history.push("/login")}
                                className="bg-indigo-600 text-white px-3 sm:px-5 py-2 rounded-xl hover:bg-indigo-700 transition font-semibold text-sm shadow-md shadow-indigo-600/20 whitespace-nowrap"
                            >
                                {t("label_iniciar_sesion")}
                            </button>
                        </div>
                    </div>

                    {mobileNavOpen && (
                        <div className="sm:hidden border-t border-slate-100 py-3 flex flex-col gap-2 px-1">
                            <button
                                type="button"
                                onClick={() => {
                                    history.push("/landing");
                                    setMobileNavOpen(false);
                                }}
                                className="text-left px-3 py-2 rounded-lg text-slate-700 hover:bg-slate-50 font-medium"
                            >
                                {t("te_register_back")}
                            </button>
                        </div>
                    )}

                    <div className="sm:hidden border-t border-slate-100 py-2 flex justify-center">
                        <LanguageDropdown />
                    </div>
                </div>
            </header>

            <main className="relative px-4 sm:px-6 lg:px-8 pt-10 pb-16 sm:pt-14 sm:pb-24">
                <div className="max-w-xl mx-auto">
                    <span className="inline-flex items-center gap-2 mb-4 px-4 py-1.5 rounded-full text-sm font-semibold bg-indigo-100 text-indigo-800 border border-indigo-200/60">
                        <span aria-hidden>📅</span>
                        {t("te_nav_join")}
                    </span>
                    <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight leading-tight mb-3">
                        {t("te_register_title")}
                    </h1>
                    <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-8">
                        {t("te_register_subtitle")}
                    </p>

                    <div className="relative rounded-3xl border border-slate-200/80 bg-white p-6 sm:p-9 shadow-xl shadow-slate-900/5 overflow-visible">
                        <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-indigo-500/5 to-violet-500/10 pointer-events-none" />
                        <form
                            onSubmit={handleRegisterSubmit}
                            className="relative space-y-6"
                            noValidate
                            autoComplete="off"
                        >
                            <Field
                                label={t("te_register_name")}
                                id="reg-name"
                                value={name}
                                onChange={(v) => {
                                    setName(v);
                                    clearFieldError("name");
                                }}
                                placeholder={t("te_register_name_ph")}
                                autoComplete="name"
                                required={true}
                                errorMessage={fieldErrors.name}
                                maxLength={charLimits.name}
                            />
                            <Field
                                label={t("te_register_phone")}
                                id="reg-phone"
                                type="tel"
                                value={phone}
                                onChange={(v) => {
                                    setPhone(v.replace(/\D/g, ""));
                                    clearFieldError("phone");
                                }}
                                placeholder={t("te_register_phone_ph")}
                                autoComplete="tel"
                                required={true}
                                errorMessage={fieldErrors.phone}
                                inputMode="numeric"
                                pattern="[0-9]*"
                                maxLength={charLimits.phone}
                            />
                            <Field
                                label={t("te_register_email")}
                                id="reg-email"
                                type="email"
                                value={email}
                                onChange={(v) => {
                                    setEmail(v);
                                    clearFieldError("email");
                                }}
                                placeholder={t("te_register_email_ph")}
                                autoComplete="email"
                                required={true}
                                errorMessage={fieldErrors.email}
                                maxLength={charLimits.email}
                            />
                            <div>
                                <label htmlFor="reg-date" className="block text-sm font-semibold text-slate-800 mb-1.5">
                                    {t("te_register_date")}<span className="text-red-500">*</span>
                                </label>
                                <p className="text-xs text-slate-500 mb-2">{t("te_register_date_help")}</p>
                                <Calendar
                                    id="reg-date"
                                    name="preferred_appointment_date"
                                    value={preferredDate}
                                    onChange={(v) => {
                                        setPreferredDate(v);
                                        clearFieldError("date");
                                    }}
                                    format="MM-DD-YYYY"
                                    min={minAppointmentDate}
                                    required={true}
                                    externalError={fieldErrors.date}
                                    maxLength={charLimits.date}
                                />
                            </div>
                            <div>
                                <label htmlFor="reg-expect" className="block text-sm font-semibold text-slate-800 mb-1.5">
                                    {t("te_register_expectations")}
                                </label>
                                <textarea
                                    id="reg-expect"
                                    rows={4}
                                    value={expectations}
                                    onChange={(e) => setExpectations(e.target.value)}
                                    placeholder={t("te_register_expectations_ph")}
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-900 shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none transition resize-y min-h-[7rem] text-base"
                                    maxLength={charLimits.expectations}
                                    aria-describedby="reg-expect-counter"
                                />
                                <CharCounter
                                    id="reg-expect-counter"
                                    used={expectations.length}
                                    max={charLimits.expectations}
                                />
                            </div>
                            <button
                                type="submit"
                                className="w-full sm:w-auto min-w-[200px] inline-flex justify-center items-center gap-2 bg-indigo-600 text-white px-8 py-3.5 rounded-xl text-base font-semibold hover:bg-indigo-700 transition shadow-lg shadow-indigo-600/25"
                            >
                                {t("te_register_submit")}
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                </svg>
                            </button>
                        </form>
                    </div>
                </div>
            </main>

            <footer className="relative bg-slate-950 text-slate-300 py-10 px-4 sm:px-6 lg:px-8 pb-24 sm:pb-10">
                <div className="max-w-6xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-6 text-center sm:text-left">
                    <div>
                        <p className="text-white font-bold text-lg">{t("te_brand")}</p>
                        <p className="text-sm text-slate-400 mt-2 max-w-md leading-relaxed">{t("te_footer_line")}</p>
                    </div>
                    <button
                        type="button"
                        onClick={() => history.push("/landing")}
                        className="text-sm text-indigo-300 hover:text-indigo-200 font-medium underline-offset-2 hover:underline"
                    >
                        {t("te_register_back")}
                    </button>
                </div>
                <div className="max-w-6xl mx-auto border-t border-slate-800 mt-8 pt-6 text-center text-sm text-slate-500">
                    © {currentYear} {t("te_brand")}. {t("te_footer_rights")}
                </div>
            </footer>

            {whatsappUrl && (
                <a
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="fixed bottom-5 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500 text-white shadow-lg shadow-emerald-900/30 hover:bg-emerald-400 hover:scale-105 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-emerald-600"
                    aria-label={t("te_whatsapp_fab_aria")}
                >
                    <WhatsAppIcon className="w-8 h-8" />
                </a>
            )}

            <ProcessingDialog visible={processingVisible} message={t("alert_procesando_solicitud")} />
            <ToastComponent
                visible={toastVisible}
                message={toastMessage}
                type={toastType}
                onClose={() => setToastVisible(false)}
            />
        </div>
    );
}

function Field({
    label,
    id,
    type = "text",
    value,
    onChange,
    placeholder,
    autoComplete,
    required = false,
    errorMessage = "",
    inputMode,
    pattern,
    maxLength,
}) {
    const errorId = `${id}-error`;
    const counterId = `${id}-counter`;
    const hasErr = Boolean(errorMessage);
    const usedLen = String(value ?? "").length;
    const describedBy = [hasErr ? errorId : null, maxLength != null && maxLength > 0 ? counterId : null]
        .filter(Boolean)
        .join(" ");
    return (
        <div>
            <label htmlFor={id} className="block text-sm font-semibold text-slate-800 mb-1.5">
                {label}
                {required && <span className="text-red-500">*</span>}
            </label>
            <input
                id={id}
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                autoComplete={autoComplete}
                inputMode={inputMode}
                pattern={pattern}
                maxLength={maxLength}
                aria-invalid={hasErr}
                aria-required={required}
                aria-describedby={describedBy || undefined}
                className={`w-full rounded-xl border bg-white px-4 py-3 text-slate-900 placeholder:text-slate-400 shadow-sm focus:outline-none focus:ring-2 transition text-base ${hasErr
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                    : "border-slate-200 focus:border-indigo-500 focus:ring-indigo-500/20"
                    }`}
            />
            <CharCounter id={counterId} used={usedLen} max={maxLength} />
            {hasErr && (
                <p id={errorId} className="mt-1.5 text-sm text-red-600" role="alert">
                    {errorMessage}
                </p>
            )}
        </div>
    );
}

function CharCounter({ id, used, max }) {
    const { t } = useTranslation();
    if (max == null || max <= 0) {
        return null;
    }
    const left = Math.max(0, max - used);
    return (
        <p
            id={id}
            className="mt-1.5 text-right text-xs tabular-nums text-slate-500"
            aria-live="polite"
        >
            {t("te_char_count", { used, max, left })}
        </p>
    );
}

function WhatsAppIcon({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.883 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
    );
}
